// FP 간트 — 좌측 컬럼 값 필터(헤더 깔때기 아이콘 → 팝오버)
//
// old 자원 간트의 컬럼 필터(TreeGrid `Kind:"Filter"` 행 + `Defaults` 드롭다운) 대응.
// 설계·시안: reports/gantt-implementation.md §7-13, prototypes/gantt-resource-column-filter.html
//
// **서버 조회 조건이 아니라 "조회된 결과 안에서 표시 행을 좁히는" 클라이언트 필터다.**
// 값 목록은 화면이 넘겨주는데, 그 출처는 **planScope 전체가 아니라 지금 조회된 행의 distinct 값**이어야
// 한다(old `Defaults`의 `*RowsAll`과 같은 의미). 조회조건의 자원 멀티셀렉트와 역할이 갈리는 지점이다.
//
// 트리거(깔때기 아이콘)는 `GanttFrame`의 헤더 셀이 그린다 — 좌측 pane은 `pointerEvents:none`이라
// 클릭을 못 받고, 헤더는 DOM이라 받을 수 있다. 이 컴포넌트는 **팝오버만** 담당한다.

import { useEffect, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Paper from "@mui/material/Paper";
import Popover from "@mui/material/Popover";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { UI } from "@plannel/pages/factory-plan/common/gantt/ganttConstants";

/**
 * props
 *  - anchorEl: 헤더 깔때기 아이콘 DOM (null이면 닫힘)
 *  - label: 검색 입력 라벨(=컬럼 라벨). 랭팩 번역된 문자열을 그대로 받는다
 *  - options: [{ value, label }] — **조회된 결과의 distinct 값**
 *  - value: Set<string> — 커밋된 필터. **빈 Set = 필터 없음**
 *  - onCommit: (Set<string>) => void
 *  - onClose: () => void
 */
const GanttColumnFilter = ({ t, anchorEl, label, options = [], value, onCommit, onClose }) => {
  const open = Boolean(anchorEl);
  // 팝오버 안에서 편집 중인 선택. **커밋 전에는 화면을 건드리지 않는다** — 체크할 때마다 적용하면
  // 고른 값만 남으면서 **목록이 발밑에서 사라진다**(자기 자신을 필터링하는 문제).
  const [draft, setDraft] = useState(() => new Set());
  const [keyword, setKeyword] = useState("");

  // 열 때 현재 필터를 draft로 복사한다. **빈 필터(=전체)는 "전부 체크"로 보여준다** — 사용자는
  // "전체에서 빼는" 방식으로 생각한다(old Defaults도 전체 목록이 켜진 상태로 열렸다).
  useEffect(() => {
    if (!open) return;
    setDraft(new Set(value.size > 0 ? value : options.map(option => option.value)));
    setKeyword("");
  }, [open, value, options]);

  const visible = useMemo(() => {
    const needle = keyword.trim().toUpperCase();
    if (!needle) return options;
    return options.filter(option => `${option.value} ${option.label}`.toUpperCase().includes(needle));
  }, [options, keyword]);

  const toggle = (optionValue, checked) => {
    setDraft(prev => {
      const next = new Set(prev);
      if (checked) next.add(optionValue);
      else next.delete(optionValue);
      return next;
    });
  };

  const commit = () => {
    // **정규화 2건** — 안 하면 "필터 없음"이 두 가지 상태로 갈려 아이콘이 잘못 활성된다:
    //   ① 전부 체크 = 필터 없음(빈 Set)
    //   ② 전부 해제 = 필터 없음. "아무것도 안 보이기"는 쓸모가 없고 old도 `*FilterOff`로 해제였다
    const all = draft.size === 0 || draft.size === options.length;
    onCommit(all ? new Set() : new Set(draft));
    onClose();
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      slotProps={{ paper: { sx: { mt: 0.5, borderRadius: 2 } } }}
    >
      <Paper elevation={0} sx={{ width: 260, p: 1.5, pt: 2 }}>
        <TextField
          size="small"
          fullWidth
          autoFocus
          label={label}
          value={keyword}
          onChange={(event) => setKeyword(event.target.value)}
          onKeyDown={(event) => { if (event.key === "Enter") commit(); }}
        />

        {/* 값 목록 — 100개 규모라 가상화 없이 스크롤로 충분하다(의존성 추가 금지 제약과도 맞다). */}
        <Box sx={{ maxHeight: 208, overflowY: "auto", mt: 1, mx: -0.5 }}>
          {visible.length === 0 ? (
            <Typography variant="caption" sx={{ display: "block", px: 1, py: 1.25, color: "text.secondary" }}>
              {t("MSG_NoData")}
            </Typography>
          ) : visible.map(option => (
            <FormControlLabel
              key={option.value}
              sx={{
                display: "flex", width: "100%", m: 0, px: 0.5, borderRadius: 1,
                "&:hover": { backgroundColor: UI.rowHover }
              }}
              control={
                <Checkbox
                  size="small"
                  sx={{ p: 0.375 }}
                  checked={draft.has(option.value)}
                  onChange={(event) => toggle(option.value, event.target.checked)}
                />
              }
              label={option.label}
              slotProps={{ typography: { sx: { fontSize: UI.cellFontSize, whiteSpace: "nowrap" } } }}
            />
          ))}
        </Box>

        {/* 모두 선택은 **검색으로 보이는 것만** 대상이다 — 검색해서 좁힌 뒤 한 번에 켜는 흐름이 자연스럽다. */}
        <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end", mt: 1.25 }}>
          <Button size="small" color="inherit" onClick={() => setDraft(prev => {
            const next = new Set(prev);
            visible.forEach(option => next.add(option.value));
            return next;
          })}
          >
            {t("selectAll")}
          </Button>
          <Button size="small" color="inherit" onClick={() => setDraft(new Set())}>{t("clearAll")}</Button>
          <Button size="small" variant="contained" onClick={commit}>{t("confirm")}</Button>
        </Box>
      </Paper>
    </Popover>
  );
};

export default GanttColumnFilter;
